import * as yup from 'yup'
import React, { useEffect, useState, useRef } from 'react'
import HomePage from 'components/templates/navigationPageTemplates/HomePage'
import { Typography, Grid, Icon } from '@mui/material'
import { useFormik } from 'formik'
import { getUpdateLocalUser } from 'utils/localStorage'
import { PERMISSION_LEVEL } from 'routes/constants'
import { updateProfile } from 'services/userServices'
import { getGoalInfoById, getVirtuesFromGoals } from 'utils/getSupportingInfo'
import { getConstant } from 'services/constantServices'
import { getFullName } from 'utils/formatText'
import { isAllowedFeature, permitted } from 'utils/permissionCheck'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { useTranslation } from 'react-i18next'
import { CONSTANTS_OPTIONS, FEATURE_KEYS } from 'services/constants'
import {
  ContainerLarge,
  ParentGridContainer
} from 'components/atoms/container/ResponsiveContainers'
import { ColStack } from 'components/atoms/container/InCardStack'
import {
  ProfileRow,
  ProfileTextInput,
  ProfileOnboardingQuestionSelect,
  ProfileSnackBar
} from 'components/molecules/user/ProfileMolecules'
import SectionDivider from 'components/atoms/SectionDivider'
import CurrentGoalSection from 'components/molecules/user/CurrentGoalSection'
import ProfileAvatarSection from 'components/molecules/user/ProfileAvatarSection'
import ProfileSkillSelect from 'components/molecules/user/ProfileSkillSelect'
import EmojiPeopleRoundedIcon from '@mui/icons-material/EmojiPeopleRounded'
import DomainRoundedIcon from '@mui/icons-material/DomainRounded'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { ReactComponent as PersonPlayIcon } from 'assets/images/personPlay.svg'
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined'
import { ReactComponent as NatureIcon } from 'assets/images/nature.svg'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import { ReactComponent as Graph2Icon } from 'assets/images/graph2.svg'
import { getLocalUser } from 'utils/localStorage'
import { validationsRules } from 'utils/formValidations'
import RowStack from 'components/atoms/container/RowStack'
import ChangePasswordButton from 'components/organisms/user/ChangePasswordButton'
import DeleteAccountButton from 'components/organisms/user/DeleteAccountButton'

const UserProfile = () => {
  const { t } = useTranslation()
  const [user, setUser] = useState({})
  const [goal, setGoal] = useState(null)
  const [cohortName, setCohortName] = useState('')
  const [hasLicense, setHasLicense] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [requestSuccess, setRequestSuccess] = useState(false)
  const [virtues, setVirtues] = useState([])
  const [experienceOptions, setExperienceOptions] = useState([])
  const [seniorityOptions, setSeniorityOptions] = useState([])
  const userInfo = getLocalUser()
  const [onboardingAnswers, setOnboardingAnswers] = useState(
    userInfo.onboardingAnswers || {}
  )
  const [profileImage, setProfileImage] = useState(userInfo.profile_image)
  const isCohortUser = userInfo?.isCohortUser || false
  const enableCompanyNameInput =
    userInfo.organization?.id && !userInfo.organization.name && hasLicense
  const showPath = hasLicense && isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT)

  const fetchUpdatedUserData = async () => {
    const updatedUser = await getUpdateLocalUser()
    setUser(updatedUser)
    return updatedUser
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      const updatedUser = await fetchUpdatedUserData()
      const { userStates } = updatedUser
      if (userStates.currentGoalId) {
        const goalInfo = await getGoalInfoById(userStates.currentGoalId)
        setGoal(goalInfo)
        if (updatedUser?.isCohortUser && goalInfo?.cohortSession) {
          setCohortName(goalInfo.cohortSession?.cohortName || '')
        }
      }
      setHasLicense(permitted(PERMISSION_LEVEL.LICENSED))
      const questionRes = await getConstant(
        CONSTANTS_OPTIONS.ONBOARDING_QUESTIONNAIRE
      )
      setExperienceOptions(questionRes?.[1].options)
      setSeniorityOptions(questionRes?.[0].options)
      const virtuesFromGoals = await getVirtuesFromGoals()
      setVirtues(virtuesFromGoals)
      setLoading(false)
    }
    fetchData()
  }, [])

  let patchController = useRef(null)
  const formik = useFormik({
    initialValues: {
      firstName: userInfo.first_name || '',
      lastName: userInfo.last_name || '',
      companyName: userInfo.organization?.name || '',
      onboardingAnswers: userInfo.onboardingAnswers || {},
      department: userInfo.department || '',
      job: userInfo.job || '',
      profileImage: null,
      removeProfileImage: false
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      firstName: validationsRules.text,
      lastName: validationsRules.text,
      companyName: validationsRules.text,
      department: validationsRules.text,
      job: validationsRules.text
    }),
    onSubmit: async values => {
      setShowNotification(false)
      const data = new FormData()
      if (values.profileImage) {
        data.append('profile_image', values.profileImage)
      } else if (values.removeProfileImage) {
        data.append('removeProfileImage', true)
      }
      data.append('first_name', values.firstName)
      data.append('last_name', values.lastName)
      data.append('department', values.department)
      data.append('job', values.job)
      data.append('onboardingAnswers', JSON.stringify(values.onboardingAnswers))
      if (enableCompanyNameInput) {
        data.append('organization_name', values.companyName)
      }

      // Abort previous request
      if (patchController.current) patchController.current.abort()
      // Create new controller
      patchController.current = new AbortController()

      const res = await updateProfile(data, {
        signal: patchController.current.signal
      })
      setShowNotification(true)
      if (res?.message !== 'canceled') {
        if (res.status === 200) {
          setRequestSuccess(true)
          await fetchUpdatedUserData()
        } else {
          setRequestSuccess(false)
        }
      }
    }
  })

  const submitTimeout = useRef()
  const clearSubmitTimeout = () => {
    if (submitTimeout.current) {
      clearTimeout(submitTimeout.current)
      submitTimeout.current = null
    }
  }
  useEffect(() => {
    if (formik.dirty) {
      clearSubmitTimeout()
      submitTimeout.current = setTimeout(() => {
        formik.submitForm()
      }, 1500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values])

  const updateOnboardingAnswers = (questionId, value) => {
    const newAnswers = { ...onboardingAnswers }
    newAnswers[questionId] = value
    setOnboardingAnswers(newAnswers)
    formik.setFieldValue('onboardingAnswers', newAnswers)
  }

  const handleAnswerChange = questionId => {
    return event => {
      updateOnboardingAnswers(questionId, parseInt(event.target.value, 10))
    }
  }

  const uploadImage = e => {
    const file = e?.target?.files?.[0]
    if (file) {
      const url = URL.createObjectURL(file)
      setProfileImage(url)
      formik.setFieldValue('profileImage', file)
      formik.setFieldValue('removeProfileImage', false)
    } else {
      setProfileImage(null)
      formik.setFieldValue('profileImage', undefined)
      formik.setFieldValue('removeProfileImage', true)
    }
  }

  return (
    <HomePage customizedBodyWidth={true}>
      <ContainerLarge>
        <ParentGridContainer maxWidth='sm'>
          <Grid size={3}>
            <ProfileAvatarSection
              user={user}
              profileImage={profileImage}
              uploadImage={uploadImage}
            />
          </Grid>

          <Grid size={9}>
            <ColStack spacing={12}>
              <ColStack spacing={0}>
                <Typography variant='h1'>
                  {getFullName(user)} / {t('headers.profile')}
                </Typography>
                <Typography>{user.email}</Typography>
              </ColStack>

              <SectionDivider />

              {showPath && (
                <>
                  <CurrentGoalSection
                    goal={goal}
                    isCohortUser={user?.isCohortUser}
                  />
                  <SectionDivider />
                </>
              )}

              <ColStack spacing={4}>
                <ProfileRow
                  icon={<EmojiPeopleRoundedIcon />}
                  label={t('profile.name')}>
                  <RowStack spacing={4}>
                    <ProfileTextInput
                      placeholder={t('profile.enterYourFirstName')}
                      formik={formik}
                      attributeName='firstName'
                    />
                    <ProfileTextInput
                      placeholder={t('profile.enterYourLastName')}
                      formik={formik}
                      attributeName='lastName'
                    />
                  </RowStack>
                </ProfileRow>

                {hasLicense && (
                  <ProfileRow
                    icon={<DomainRoundedIcon />}
                    label={t('profile.organization')}>
                    <ProfileTextInput
                      placeholder={t('company.enterYourCompanyName')}
                      formik={formik}
                      attributeName='companyName'
                      disabled={!enableCompanyNameInput}
                    />
                  </ProfileRow>
                )}

                {isCohortUser && (
                  <ProfileRow
                    icon={<GroupsOutlinedIcon />}
                    label={t('overview.cohort')}>
                    <ProfileTextInput value={cohortName} disabled />
                  </ProfileRow>
                )}

                <ProfileRow
                  icon={
                    <Icon>
                      <PersonPlayIcon />
                    </Icon>
                  }
                  label={t('profile.skills')}>
                  {virtues?.length > 0 && (
                    <ProfileSkillSelect
                      virtues={virtues}
                      initialValue={
                        typeof onboardingAnswers?.['skills'] !== 'undefined'
                          ? onboardingAnswers?.['skills']
                          : []
                      }
                      onChangeCallback={skills =>
                        updateOnboardingAnswers('skills', skills)
                      }
                    />
                  )}
                </ProfileRow>

                <ProfileRow
                  icon={<WorkHistoryOutlinedIcon />}
                  label={t('onboarding.experience')}>
                  {experienceOptions?.length > 0 && (
                    <ProfileOnboardingQuestionSelect
                      name='experience'
                      placeholder={t('profile.yourExperience')}
                      options={experienceOptions}
                      onChange={handleAnswerChange(1)}
                      value={
                        typeof onboardingAnswers?.[1] !== 'undefined'
                          ? onboardingAnswers?.[1]
                          : ''
                      }
                    />
                  )}
                </ProfileRow>

                <ProfileRow
                  icon={
                    <Icon>
                      <NatureIcon />
                    </Icon>
                  }
                  label={t('onboarding.seniority')}>
                  {seniorityOptions?.length > 0 && (
                    <ProfileOnboardingQuestionSelect
                      name='seniority'
                      placeholder={t('profile.yourSeniority')}
                      options={seniorityOptions}
                      onChange={handleAnswerChange(0)}
                      value={
                        typeof onboardingAnswers?.[0] !== 'undefined'
                          ? onboardingAnswers?.[0]
                          : ''
                      }
                    />
                  )}
                </ProfileRow>

                <ProfileRow
                  icon={<BadgeOutlinedIcon />}
                  label={t('profile.role')}>
                  <ProfileTextInput
                    placeholder={t('profile.yourRole')}
                    formik={formik}
                    attributeName='job'
                    inputProps={{ maxLength: 255 }}
                  />
                </ProfileRow>

                <ProfileRow
                  icon={
                    <Icon>
                      <Graph2Icon />
                    </Icon>
                  }
                  label={t('profile.department')}>
                  <ProfileTextInput
                    placeholder={t('profile.yourDepartment')}
                    formik={formik}
                    attributeName='department'
                  />
                </ProfileRow>
              </ColStack>

              <SectionDivider />
              <ChangePasswordButton />

              <SectionDivider />
              <DeleteAccountButton />
            </ColStack>
          </Grid>
        </ParentGridContainer>
      </ContainerLarge>
      <OverlayLoading open={loading} />
      <ProfileSnackBar
        open={showNotification}
        setOpen={setShowNotification}
        requestSuccess={requestSuccess}
      />
    </HomePage>
  )
}

export default UserProfile
